// Onderwerpclusters — één bron voor "welke pagina hoort bij welk onderwerp".
//
// Waarom dit bestaat: de site had de clusterindeling op drie plekken half
// staan — `category:` in de frontmatter (op 21 van de ~100 bestanden, nergens
// uitgelezen), het megamenu in app/components/nav/Header.vue, en handmatig
// getypte "Gerelateerde artikelen"-blokken onderaan de markdown. Daardoor
// stuurde elke contentpagina zijn CTA naar /vergelijk-verkoop/, ook de
// aankoop- en taxatiepagina's, en wees geen enkele spoke consequent naar zijn
// eigen hub.
//
// Een cluster bestaat uit:
//   - hub:   de pagina die de kopterm moet winnen; alle spokes linken erheen
//   - cta:   het aanvraagformulier dat bij de intentie past
//   - label: hoe we het cluster in de UI noemen
//
// Pagina's koppelen zich aan een cluster via `category:` in de frontmatter.

export type ClusterKey
  = | 'aankoopmakelaar'
    | 'verkoopmakelaar'
    | 'huis-verkopen'
    | 'huis-kopen'
    | 'taxatie'
    | 'makelaarskosten'

export type BrokerType = 'vergelijk-aankoop' | 'vergelijk-verkoop' | 'vergelijk-taxatie'

export type Cluster = {
  key: ClusterKey
  label: string
  hub: string
  cta: BrokerType
  ctaLabel: string
}

export const CLUSTERS: Record<ClusterKey, Cluster> = {
  'aankoopmakelaar': {
    key: 'aankoopmakelaar',
    label: 'Aankoopmakelaar',
    hub: '/aankoopmakelaar/',
    cta: 'vergelijk-aankoop',
    ctaLabel: 'Vergelijk aankoopmakelaars',
  },
  'verkoopmakelaar': {
    key: 'verkoopmakelaar',
    label: 'Verkoopmakelaar',
    hub: '/verkoopmakelaar/',
    cta: 'vergelijk-verkoop',
    ctaLabel: 'Vergelijk verkoopmakelaars',
  },
  'huis-verkopen': {
    key: 'huis-verkopen',
    label: 'Huis verkopen',
    hub: '/verkoopmakelaar/',
    cta: 'vergelijk-verkoop',
    ctaLabel: 'Vergelijk verkoopmakelaars',
  },
  'huis-kopen': {
    key: 'huis-kopen',
    label: 'Huis kopen',
    hub: '/aankoopmakelaar/',
    cta: 'vergelijk-aankoop',
    ctaLabel: 'Vergelijk aankoopmakelaars',
  },
  'taxatie': {
    key: 'taxatie',
    label: 'Taxatie',
    hub: '/taxateur/',
    cta: 'vergelijk-taxatie',
    ctaLabel: 'Vergelijk taxateurs',
  },
  'makelaarskosten': {
    key: 'makelaarskosten',
    label: 'Makelaarskosten',
    hub: '/makelaar-kosten/',
    cta: 'vergelijk-verkoop',
    ctaLabel: 'Vergelijk makelaars op tarief',
  },
}

// Waar geen cluster bekend is, valt de CTA terug op het brede vergelijken.
// Bewust verkoop: dat is de grootste leadstroom en de oude hard-coded waarde.
export const DEFAULT_CLUSTER: Cluster = CLUSTERS['verkoopmakelaar']

// hasOwn i.p.v. `in`: anders matchen prototype-keys als "constructor".
export const isClusterKey = (value: unknown): value is ClusterKey =>
  typeof value === 'string' && Object.hasOwn(CLUSTERS, value)

const isBrokerType = (value: unknown): value is BrokerType =>
  value === 'vergelijk-aankoop' || value === 'vergelijk-verkoop' || value === 'vergelijk-taxatie'

const CTA_LABELS: Record<BrokerType, string> = {
  'vergelijk-aankoop': 'Vergelijk aankoopmakelaars',
  'vergelijk-verkoop': 'Vergelijk verkoopmakelaars',
  'vergelijk-taxatie': 'Vergelijk taxateurs',
}

// Onderwerp en intentie vallen niet altijd samen: /tip/kosten-aankoopmakelaar/
// hoort onderwerpelijk bij makelaarskosten, maar de lezer is een koper. Met
// `brokerType` in de frontmatter overschrijft een pagina de CTA van zijn cluster
// zonder uit het cluster te vallen.
export const resolveCluster = (category?: unknown, brokerType?: unknown): Cluster => {
  const cluster = isClusterKey(category) ? CLUSTERS[category] : DEFAULT_CLUSTER
  if (!isBrokerType(brokerType) || brokerType === cluster.cta) return cluster
  return { ...cluster, cta: brokerType, ctaLabel: CTA_LABELS[brokerType] }
}
